import { getI18n } from "react-i18next";
import { BookingStatusType } from "../types/baseTypes";
import { Reservation } from "../types/order";
import { Rental } from "../types/rental";
import i18nInstance from "i18next";
import {BookingSummary as BookingSummaryProps} from "../types/bookingCreation";
import moment from "moment/moment";
import momentTz from "moment-timezone";
import { hasOvertimeAllowance, isOvertime } from "./overtime";
import { isRoomFamily } from "./useCases";
import {
  atStation,
  pickedDayLabel,
  STATION_TIME_ZONE,
  stationDay,
  stationDayLabel,
} from "./stationTime";

export function convertMinutesToHours(minutes: number, fullLabel = false): string {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  let translatedHourLabel = ""
  if (fullLabel) {
    translatedHourLabel = getI18n().t(
        "book_location.confirmation.hours_full_string",
        {count: hours}
    )
  } else {
    translatedHourLabel = getI18n().t(
        "book_location.confirmation.hours"
    )
  }
  const translatedMinutesLabel = getI18n().t("book_location.confirmation.minutes");

  if (hours > 0 && remainingMinutes > 0) {
    return `${hours} ${translatedHourLabel} ${remainingMinutes} ${translatedMinutesLabel}`;
  } else if (hours > 0) {
    return `${hours} ${translatedHourLabel}`;
  } else {
    return `${remainingMinutes} ${translatedMinutesLabel}`;
  }
}
type OrderItem = { id: string };

export function getIdString(items: OrderItem[]): string {
  return items?.map((item) => item?.id)?.join(",");
}

export const isEmptyObject = (obj: object): boolean => {
  return Object.keys(obj).length === 0;
};

/**
 * #2040: "is anything owed right now" is `isPaid`, not `wasPaid`.
 *
 * `wasPaid` is sticky — true forever once a booking has ever been paid — so an
 * admin edit into a pricier slot left this reading "booked" while money was
 * outstanding, and the check-in gate in `OrdersPage` agreed. The customer saw a
 * confirmed booking and could take the boards without settling the difference.
 *
 * `hasOpenBalance` is the one place that question is answered, so the badge and
 * the gate cannot drift apart again. It is the fifth site of the rule #1889
 * consolidated (`isBookingReserved`) — the admin backends import that function;
 * this frontend cannot, so it mirrors it and says so.
 *
 * Absent `isPaid` (a response cached from before the field shipped) means "no
 * balance known" and keeps today's behaviour, never a false accusation.
 */
export const hasOpenBalance = (booking: {
  wasPaid?: boolean;
  isPaid?: boolean;
  contractConcludedAt?: string | null;
  paymentStatus?: string | null;
}): boolean => {
  // #1704: the contract axis wins over the money, exactly as it does in the
  // backend's `isBookingReserved` this function mirrors. A postpay booking is
  // `unpaid` BY DESIGN — the customer concluded the contract and settles by
  // invoice after use — so reading the balance alone would call it reserved
  // forever and lock it out of check-in. What this function is asked, at both
  // its call sites, is "is the customer entitled to use this booking"; a
  // concluded contract answers yes whatever is owed.
  if (booking.contractConcludedAt) return false;
  // A SETTLED payment status wins over a stale `is_paid`: the old backend's
  // admin edit wrote `payment_status = 'paid'` without `is_paid`, so a booking
  // the admin showed "Gebucht / Bezahlt" read "Reserviert / Zahlung offen" here
  // with check-in locked.
  //
  // Deliberately narrower than the backends' `isBookingReserved`, which treats
  // every status other than unpaid / partially paid as not reserved. This
  // function gates the guest's ENTITLEMENT (check-in, rental start), so only
  // paid / overpaid open it; `pending`, `refunded`, `disputed` keep the reading
  // they had — a pending payment must not let the guest start. Absent (a cached
  // response from before the field) keeps the previous reading too.
  const status = booking.paymentStatus?.toLowerCase();
  if (status === "paid" || status === "overpaid") return false;
  if (booking.isPaid !== undefined) return booking.isPaid === false;
  return booking.wasPaid === false;
};

/**
 * Is a venue reservation still awaiting the operator's decision?
 *
 * The ticket note that answers this says "your reservation is pending review,
 * you will hear from us after approval" — true for a submitted request, and a
 * contradiction of the "Gebucht" chip above it once the reservation is decided
 * (#2352). Two facts decide it, and neither is the balance alone:
 *
 * - `contractConcludedAt` — the customer accepted the offer, so the booking is
 *   binding. A postpay booking is concluded and unpaid at the same time, on
 *   purpose, which is exactly why the money axis cannot answer this.
 * - `wasPaid` — a prepaid venue booking was decided at payment.
 *
 * One place, because `OrdersPage` and `BookingSummaryNote` both used to decide
 * it and only one of them looked at the contract.
 */
export const isVenueReviewPending = (booking: {
  wasPaid?: boolean;
  contractConcludedAt?: string | null;
}): boolean => {
  if (booking.contractConcludedAt) return false;
  return booking.wasPaid !== true;
};

/** Venue and apartment: the status follows the booked window alone. */
const roomStatus = (now: Date, orderItem: any): BookingStatusType => {
  if (now < new Date(orderItem?.fromDate)) return "booked";
  if (now >= new Date(orderItem?.fromDate) && now <= new Date(orderItem?.toDate)) return "active";
  // No room-rental tenant runs check-in/check-out through us today, so
  // "never used" is not observable here and a past window simply means
  // finished. The axis is usage-flow adoption, NOT the use case (see
  // docs/architecture/check-in-check-out-flow.md — overtime/expired
  // derive from usage events, across all use cases): once a room tenant
  // adopts the flow, this must key on those events instead of the clock.
  // Matches both admin backends.
  return "finished";
};

/** A paid booking whose rental was never started. */
const unstartedStatus = (now: Date, orderItem: any): BookingStatusType => {
  // #2084: the paid counterpart of `incompleted` above. No rental was ever
  // created and the window has passed, so the customer paid and never used
  // the slot — which until now read "finished", the same word as a booking
  // that ran to completion.
  //
  // Mirrors `isBookingExpired` in both admin backends. This frontend cannot
  // import them, so the rule is restated and says so — the same arrangement
  // as `hasOpenBalance` above. Change one, change all four.
  //
  // `toDate`, not `fromDate`: a booking still inside its window has not
  // expired, it is merely late to start. And `owes` is already false here —
  // the two branches at the top took every unpaid case — so reaching this
  // line means the booking is paid.
  const endsAt = orderItem?.toDate ? new Date(orderItem.toDate) : null;
  if (endsAt && !Number.isNaN(endsAt.getTime()) && now > endsAt) {
    return "expired";
  }
  return "booked";
};

/** A booking whose rental exists: started, finished or cancelled. */
const rentalStatus = (rental: Rental, serviceType: string, orderItem: any, now: Date): BookingStatusType => {
  // Consider both camelCase and snake_case finish fields
  const hasFinishedDate = Boolean((rental as any).finishedAtDate || (rental as any).finished_at);

  if (!rental.isActive && hasFinishedDate) {
    return "finished";
  }

  if (["item_retrieved_confirmed"].includes(rental.state ?? "")) {
    return "finished";
  }

  if (rental.isActive) {
    // Overtime: active, not finished, past the scheduled end. No grace in the
    // onset — grace is fee-only (see the usage lifecycle state model in
    // docs/architecture/check-in-check-out-flow.md).
    //
    // Which use cases that covers is `hasOvertimeAllowance`, the mirror of the
    // backend's list — so the ticket says "overtime" exactly when the door
    // still grants the overtime opening. Spelled `rental || sup` inline here
    // until 2026-09-14, which is why a storage booking past its end read
    // "active" on the customer's own ticket.
    if (hasOvertimeAllowance(serviceType) && isOvertime(orderItem?.toDate, true, false, now)) {
      return "overtime";
    }
    return "active";
  }

  if (!hasFinishedDate) {
    return "canceled";
  }

  // Default case (though all cases should be covered above)
  return "finished";
};

export const getBookingStatusType = (rental: Rental, serviceType: string, orderItem: any, wasPaid: boolean, isPaid?: boolean, contractConcludedAt?: string | null, paymentStatus?: string | null): BookingStatusType => {
  const now = new Date();
  const owes = hasOpenBalance({ wasPaid, isPaid, contractConcludedAt, paymentStatus });
  const unstarted = !rental || isEmptyObject(rental);
  // `new Date(...)`, not the raw value: `fromDate` arrives as an ISO STRING,
  // and `Date > string` coerces the string with ToNumber, which is NaN — so
  // the comparison was always false and this branch never ran. An unpaid past
  // booking read "Reserved" here while admin read "Incompleted" (#2084). Every
  // other date comparison in this function already wraps it.
  if (owes && unstarted) {
    return now > new Date(orderItem?.fromDate) ? "incompleted" : "reserved";
  }

  if (isRoomFamily(serviceType)) {
    return roomStatus(now, orderItem);
  }

  return unstarted ? unstartedStatus(now, orderItem) : rentalStatus(rental, serviceType, orderItem, now);
};

export const checkIfOrderCanBeStarted = (
    order: BookingSummaryProps,
    gracePeriodMinutes: number = 0
) => {
  const currentLocationTime = new Date();
  const startDate = moment(order.items[0].fromDate);
  const endDate = moment(order.items[0].toDate);
  const endDateWithGracePeriod = moment(endDate).add(gracePeriodMinutes, "minutes");
  const locationDate = moment(currentLocationTime);
  return locationDate.isSameOrAfter(startDate) && locationDate.isSameOrBefore(endDateWithGracePeriod);
};

export function formatDateTime(input: string) {
  // Split the input into the date and the time portion
  const lastSpaceIndex = input.lastIndexOf(" "); // Find the last space before 'am' or 'pm'

  // Extract the date and time parts
  const date = input.slice(0, lastSpaceIndex - 5); // The date portion (subtracting 5 to exclude the time)
  const time = input.slice(lastSpaceIndex - 5); // The time portion, including am/pm

  // Return the formatted string with a vertical bar
  return `${date} | ${time}`;
}

export function transformReservations(reservations: Reservation[]): { id: string }[] {
  return reservations?.map((reservation) => ({ id: reservation?.id }));
}

/**
 * Helper to format currency dependending on the locale.
 * Remarks: Change format for english locale to 'en-US' if needed.
 */
export const formatCurrency = (
  value: number,
  locale: string,
  defaultCurrency = "EUR"
): string => {
  const tenantConfig = (window as any)?.tenantConfig;

  const resolvedCurrency = tenantConfig?.currency ?? defaultCurrency;

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: resolvedCurrency,
    currencyDisplay: "symbol", // Show only symbol, not code like "NOK"
  });

  let formatted = formatter.format(value);

  // Remove unwanted ISO code like "NOK" if it's still there
  const currencyCodePattern = new RegExp(`\\b${resolvedCurrency}\\b`, "g");
  formatted = formatted.replace(currencyCodePattern, "").trim();

  // Ensure correct symbol position (after number)
  if (["NOK", "EUR"].includes(resolvedCurrency)) {
    const symbol = resolvedCurrency === "NOK" ? "kr" : "€";
    formatted = formatted.replace(symbol, "").trim();
    formatted = `${formatted} ${symbol}`;
  }

  return formatted;
};

export const getEquipmentName = (
  equipmentName: string,
  equipmentLangNames?: Record<string, { title: string; description: string }>,
  returnType: "title" | "description" = "title"
) => {
  const lang = i18nInstance.language;

  if (!equipmentLangNames) {
    return equipmentName;
  }

  if (Object.keys(equipmentLangNames).includes(lang)) {
    if (Object.keys(equipmentLangNames[lang]).includes(returnType)) {
      return equipmentLangNames[lang][returnType];
    }
  }
  return equipmentName;
};


// Slug shape mirrors the backend SLUG_REGEX (lowercase alphanumeric + hyphens,
// 3–63 chars, alphanumeric at both ends).
const TENANT_SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;

/**
 * Tenant slug taken from the first path segment on the shared deployment
 * (`app.kolula.com/{slug}/...`). Returns null when there is no valid slug
 * segment (e.g. the bare domain). Pure synchronous read of `window.location`,
 * so it is safe to call anywhere `getTenantName` is used.
 */
export const getTenantSlugFromPath = (): string | null => {
  const segment = window.location.pathname.split("/").filter(Boolean)[0];
  if (!segment) return null;
  const slug = segment.toLowerCase();
  return TENANT_SLUG_REGEX.test(slug) ? slug : null;
};

/**
 * True on the shared end-user deployment (`app.kolula.com/{slug}`), driven by an
 * explicit env flag set ONLY on that Amplify app (`REACT_APP_SHARED_MODE=true`).
 *
 * We can't rely on the absence of `REACT_APP_TENANT`: the committed `.env` bakes
 * a pinned tenant into every build, and Amplify won't store an empty override to
 * unset it. So the shared app opts in explicitly; when this is true the pinned
 * `REACT_APP_TENANT` and the dev localStorage override are ignored and the
 * tenant is always resolved from the URL slug.
 */
export const isSharedDeployment = (): boolean =>
  process.env.REACT_APP_SHARED_MODE === "true";

/**
 * Resolves the active tenant. Resolution chain (legacy-safe, additive):
 *   0. Shared deployment (`REACT_APP_SHARED_MODE=true`) → always the URL slug,
 *      ignoring any pinned `REACT_APP_TENANT` / dev localStorage override.
 *   1. `REACT_APP_TENANT` set → pinned/legacy build, URL ignored. (Dev keeps the
 *      localStorage override for tenant switching.) This branch is byte-for-byte
 *      the original behavior, so legacy deployments are unchanged.
 *   2. No pinned tenant: dev localStorage override, else
 *   3. the `{slug}` from the URL path.
 */
export const getTenantName = (): string | undefined => {
  if (isSharedDeployment()) {
    return getTenantSlugFromPath() ?? undefined;
  }

  const environment = process.env.REACT_APP_ENV ?? "integration";
  const isDev = ["integration", "development"].includes(environment);
  const envTenantName = process.env.REACT_APP_TENANT;

  if (envTenantName) {
    return isDev ? (localStorage.getItem("tenant") ?? envTenantName) : envTenantName;
  }

  if (isDev) {
    const stored = localStorage.getItem("tenant");
    if (stored) return stored;
  }

  return getTenantSlugFromPath() ?? undefined;
};

/**
 * React Router basename. Only the shared deployment serves the app under a
 * `/{slug}` path prefix; legacy (pinned) deployments serve at root. The explicit
 * shared-mode flag wins over a baked-in `REACT_APP_TENANT`.
 */
export const getRouterBasename = (): string => {
  if (!isSharedDeployment() && process.env.REACT_APP_TENANT) return "/";
  const slug = getTenantSlugFromPath();
  return slug ? `/${slug}` : "/";
};

/**
 * Builds an absolute URL into this app, respecting the shared-deployment
 * basename. On the shared deployment the app is mounted under `/{slug}`, so any
 * URL handed to an external service (Stripe success / cancel / return URLs) must
 * carry that prefix — otherwise the redirect lands on the bare domain, loses the
 * tenant slug, and the app can't resolve the tenant on the way back. Legacy/
 * pinned builds serve at root, so the prefix is empty.
 */
export const buildAppUrl = (path: string): string => {
  const basename = getRouterBasename();
  const prefix = basename === "/" ? "" : basename;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${window.location.origin}${prefix}${normalizedPath}`;
};


// interface OpeningHours {
//   [key: string]: [number, number]; // [openHour, closeHour]
// }

// const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

// const openingHours: OpeningHours = {
//   mon: [8, 15],
//   tue: [8, 15],
//   wed: [8, 15],
//   thu: [8, 15],
//   fri: [8, 15],
//   sat: [8, 15],
//   sun: [8, 15],
// };

// const OPENING_TIME_INTERVAL = 0; // minutes

// const getDayOfWeek = (date: Date): number => {
//   return date.getDay(); // Sunday = 0, Monday = 1, etc.
// };

// const getDayKey = (date: Date): string => {
//   return DAYS[getDayOfWeek(date)];
// };

// const getModifiedOpeningHour = (
//     openHour: number,
//     date: Date,
//     type?: 'startingEnd' | 'startingStart' | 'endingStart' | 'endingEnd',
//     isSameDay?: boolean
// ) => {
//   let modifiedOpeningHour = openHour;
//   if (date.toISOString().slice(0, 10) === new Date().toISOString().slice(0, 10)) {
//     modifiedOpeningHour = Math.max(modifiedOpeningHour, new Date().getHours() + 1);
//   }

//   if (isSameDay) {
//     if (type === 'endingStart') {
//       modifiedOpeningHour += 1;
//     }
//   }

//   if (type === 'startingEnd') {
//     modifiedOpeningHour -= 1;
//   }

//   return modifiedOpeningHour;
// };

// const generateTimeSlots = (
//     startHour: number,
//     endHour: number,
// ): string[] => {
//   const slots: string[] = [];

//   if (startHour === endHour) {
//     return slots;
//   }

//   const startMinute = 0;

//   for (let hour = startHour; hour <= endHour; hour++) {
//     // Handle 0 interval case
//     if (OPENING_TIME_INTERVAL === 0) {
//       slots.push(`${hour.toString().padStart(2, '0')}:00`);
//     } else {
//       for (let minute = hour === startHour ? startMinute : 0; minute < 60; minute += OPENING_TIME_INTERVAL) {
//         if (hour === endHour && minute > 0) continue;
//         slots.push(
//             `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`,
//         );
//       }
//     }
//   }

//   return slots;
// };

// const getStartTimes = (
//     date: Date,
//     isSameDay: boolean
// ): string[] => {
//   const dayKey = getDayKey(date);
//   const [openHour, closeHour] = openingHours[dayKey];

//   const modifiedOpeningHour = getModifiedOpeningHour(openHour, date, 'startingStart', isSameDay);
//   const modifiedClosingHour = getModifiedOpeningHour(closeHour, date, 'startingEnd', isSameDay);

//   return generateTimeSlots(modifiedOpeningHour, modifiedClosingHour);
// };

// const getEndTimes = (
//     date: Date,
//     isSameDay: boolean
// ): string[] => {
//   const dayKey = getDayKey(date);
//   const [openHour, closeHour] = openingHours[dayKey];
//   let slots: string[] = [];

//   const modifiedOpeningHour = getModifiedOpeningHour(openHour, date, 'endingStart', isSameDay);
//   const modifiedClosingHour = getModifiedOpeningHour(closeHour, date, 'endingEnd', isSameDay);

//   // Special handling for overnight stays (if applicable)
//   slots = generateTimeSlots(modifiedOpeningHour, modifiedClosingHour);

//   return slots;
// };

// export const getAvailableBookingTimes = (
//     checkinDate: string,
//     checkoutDate: string,
// ) => {
//   // Parse input dates
//   const checkin = new Date(checkinDate);
//   const checkout = new Date(checkoutDate);

//   // Validate dates
//   if (checkin > checkout) {
//     throw new Error('Check-in date must be before check-out date');
//   }

//   const isSameDay = checkin.toISOString().slice(0, 10) === checkout.toISOString().slice(0, 10);

//   // Get available times for check-in and check-out dates
//   const availableStartTimes = getStartTimes(checkin, isSameDay);
//   const availableEndTimes = getEndTimes(checkout, isSameDay);

//   return {
//     availableStartTimes,
//     availableEndTimes,
//   };
// };

type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export interface LocationOpeningHourEntry {
  weekday: Weekday | string;
  times: {
    fromHour: number;
    toHour: number;
  };
}

type OpeningHoursMap = Partial<Record<Weekday, [number, number]>>;

// Used when no location has opening hours configured at all -
// treated as "no restrictions" so the form remains usable.
const FULL_DAY_HOURS: [number, number] = [0, 24];

const DAYS: Weekday[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/** The weekday of the calendar day the customer picked (its label, not the
 *  phone's reading of a Berlin instant). */
function getDayKey(
  pickedDay: moment.Moment,
  timeZone: string = STATION_TIME_ZONE
): Weekday {
  return DAYS[stationDay(pickedDay, timeZone).day()];
}

// Builds a per-weekday map by taking the lowest fromHour and highest toHour
// across all entries. Time slots are picked before the venue is selected,
// so this reflects the widest window any location could support per weekday.
function buildOpeningHoursMap(entries?: LocationOpeningHourEntry[]): OpeningHoursMap {
  if (!entries || entries.length === 0) return {};
  const map: OpeningHoursMap = {};
  for (const entry of entries) {
    const key = entry.weekday as Weekday;
    if (!DAYS.includes(key)) continue;
    const existing = map[key];
    if (existing) {
      map[key] = [
        Math.min(existing[0], entry.times.fromHour),
        Math.max(existing[1], entry.times.toHour),
      ];
    } else {
      map[key] = [entry.times.fromHour, entry.times.toHour];
    }
  }
  return map;
}

function resolveDayHours(
  dayKey: Weekday,
  map: OpeningHoursMap,
  hasConfiguredHours: boolean
): [number, number] | null {
  const dayHours = map[dayKey];
  if (dayHours) return dayHours;
  // No location has hours configured at all -> open all day.
  // Some locations have hours but none for this weekday -> closed.
  return hasConfiguredHours ? null : FULL_DAY_HOURS;
}

function generateHourSlots(start: number, end: number): string[] {
  const slots: string[] = [];
  for (let h = start; h < end; h++) {
    slots.push(`${h}:00`);
  }
  return slots;
}

/**
 * The last end slot ever offered is 23:00. A day open until 24 used to offer
 * "24:00", which `setTimeOnDate` rejects (no quote, booking fails) and the
 * booking URL's `TIME_PATTERN` rejects too (a reload drops it) — so it was
 * never a bookable value. Not offered, rather than resolved to the next
 * midnight: that would send a window ending on the following day, which no
 * caller has been verified against.
 */
const LAST_END_HOUR = 23;

/**
 * Start and end slots for a venue booking, in STATION time.
 *
 * `startDay`/`endDay` are the picked days (the picker's moments); their weekday
 * and "is it today?" are read from the day LABEL against the station's clock.
 * This used to take ISO strings and read them with the phone's `Date` getters,
 * so "today, future slots only" followed the phone's hour (a London phone at
 * 09:30 Berlin offered 10:00, already past at the station's 10:30 cut) and a
 * phone on a different calendar day than the station got the wrong "today".
 * `now` is a parameter so a test can pin it; `timeZone` is the location's
 * (#3219) — "today" and "the current hour" are the location's.
 */
export function getAvailableBookingTimes(
  startDay: moment.Moment,
  endDay: moment.Moment,
  selectedStartTime?: string, // e.g. "9:00"
  selectedEndTime?: string, // not used yet, but kept for flexibility
  locationOpeningHours?: LocationOpeningHourEntry[],
  now: moment.MomentInput = Date.now(),
  timeZone: string = STATION_TIME_ZONE
): { availableStartTimes: string[]; availableEndTimes: string[] } {
  const startLabel = pickedDayLabel(startDay);
  const isSameDay = startLabel === pickedDayLabel(endDay);
  const stationNow = atStation(now, timeZone);

  const map = buildOpeningHoursMap(locationOpeningHours);
  const hasConfiguredHours =
    !!locationOpeningHours && locationOpeningHours.length > 0;

  const startDayHours = resolveDayHours(getDayKey(startDay, timeZone), map, hasConfiguredHours);
  const endDayHours = resolveDayHours(getDayKey(endDay, timeZone), map, hasConfiguredHours);

  return {
    availableStartTimes: startTimesFor(
      startDayHours,
      isSameDay,
      startLabel === stationDayLabel(stationNow, timeZone) ? stationNow.hour() : null
    ),
    availableEndTimes: endTimesFor(endDayHours, isSameDay, selectedStartTime),
  };
}

/**
 * Start slots on the picked start day. `currentStationHour` is set when that
 * day is TODAY at the station: then only future slots, on the station's clock.
 * Same day: a start needs an end slot after it, and none follows 23:00.
 */
function startTimesFor(
  dayHours: [number, number] | null,
  isSameDay: boolean,
  currentStationHour: number | null
): string[] {
  if (!dayHours) return [];
  const [startOpen, openUntil] = dayHours;
  const startClose = isSameDay ? Math.min(openUntil, LAST_END_HOUR) : openUntil;
  if (currentStationHour === null) return generateHourSlots(startOpen, startClose);
  const nextHour = currentStationHour + 1;
  return nextHour < startClose
    ? generateHourSlots(Math.max(startOpen, nextHour), startClose)
    : [];
}

/**
 * End slots on the picked end day: none until a start is chosen ("0"); on the
 * same day the earliest end is one hour after the chosen start; on a later day
 * the full day.
 */
function endTimesFor(
  dayHours: [number, number] | null,
  isSameDay: boolean,
  selectedStartTime: string | undefined
): string[] {
  if (!dayHours || selectedStartTime === "0") return [];
  const [endOpen, endClose] = dayHours;
  const endSlotsUntil = Math.min(endClose, LAST_END_HOUR) + 1;
  if (selectedStartTime && isSameDay) {
    const startHour = parseInt(selectedStartTime.split(":")[0]);
    return startHour + 1 <= endClose
      ? generateHourSlots(Math.max(startHour + 1, endOpen), endSlotsUntil)
      : [];
  }
  return generateHourSlots(endOpen, endSlotsUntil);
}





// COPY of frontend/admin-ui/src/utils/index.ts `eventTypes` (the two apps
// share no package, so the catalog exists twice). DE/EN translate by ARRAY
// INDEX (`getEventType` below), so any change must land at the SAME index in
// all four arrays across both files. Tenant-configurable event types would
// retire both copies.
export const eventTypes = {
  "en": ["Seminar", "Birthday", "Wedding", "Conference", "Team event", "Public meeting", "Cultural event", "Hackathon", "Other"],
  "de": ["Seminar", "Geburtstag", "Hochzeit", "Konferenz", "Teamevent", "Bürgerversammlung", "Kulturveranstaltung", "Hackathon", "Andere"],
}

type Language = keyof typeof eventTypes;

/**
 * The language to read the event-type table in.
 *
 * `i18n.language` is NOT one of this table's two languages by construction: it
 * carries whatever was last set, and `changeLanguage` does not normalise
 * against `supportedLngs` (only the detector does). Every caller used to bridge
 * that with `as "en" | "de"` — a cast, so the compiler stopped asking, and
 * `eventTypes[language]` was `undefined` for anything else. That indexes into
 * `undefined` and takes the ticket card down with it, rather than degrading to
 * a language we do have.
 *
 * `nb` is the concrete one: the switcher offers it and `supportedLanguages`
 * lists it, gated only by no tenant having `nb` in `languages` today. Falling
 * back to English matches what the three call sites that read the table
 * directly already do (`eventTypes[...] ?? eventTypes["en"]`).
 */
export const eventTypeLanguage = (language: string | undefined): Language =>
  language && Object.prototype.hasOwnProperty.call(eventTypes, language)
    ? (language as Language)
    : "en";

export function getEventType(
  eventType: string,
  language: string | undefined
): string {
  const target = eventTypes[eventTypeLanguage(language)];

  // Find the index in English array (default if not found)
  const enIndex = eventTypes.en.indexOf(eventType as typeof eventTypes.en[number]);

  // Find the index in German array (fallback if not found in English)
  const deIndex = eventTypes.de.indexOf(eventType as typeof eventTypes.de[number]);

  // If found in English array, return corresponding German term (or English if language is English)
  if (enIndex !== -1) {
    return target[enIndex];
  }

  // If found in German array, return corresponding English term (or German if language is German)
  if (deIndex !== -1) {
    return target[deIndex];
  }

  // If not found in either, return the original string
  return eventType;
}

/**
 * The instant at which the station's wall clock (the location's zone, #3219;
 * Berlin by default) reads y-m-d h:min.
 *
 * Resolved by the tz database (moment-timezone), not by arithmetic on a
 * trial instant: the hand-rolled version read the Berlin hour of
 * `Date.UTC(y, m, d, h)`, which for 22:00 (summer) and 23:00 (all year) has
 * already wrapped past midnight in Berlin — so the offset came out ~22 h
 * and a venue booked until 22:00 on 22.10.2026 was sent, quoted and charged
 * as ending at 22:00 on the 23rd.
 */
export function getUTCFromStationClock(
  y: number,
  m: number,
  d: number,
  h: number,
  min: number,
  timeZone: string = STATION_TIME_ZONE
): number {
  return momentTz
    .tz({ year: y, month: m - 1, date: d, hour: h, minute: min }, timeZone)
    .valueOf();
}

/**
 * The station day an instant falls on, at the station wall-clock time
 * `timeString` ("HH:mm") — both read in `timeZone`, the location's (#3219).
 */
export function setTimeOnDate(
  isoDateString: string,
  timeString: string,
  timeZone: string = STATION_TIME_ZONE
): string {
    const [hours, minutes] = timeString.split(':').map(Number);

    if (
        isNaN(hours) || isNaN(minutes) ||
        hours < 0 || hours > 23 || minutes < 0 || minutes > 59
    ) {
        throw new Error('Invalid time format. Expected HH:mm');
    }

    const date = new Date(isoDateString);

    // Get year, month, day in the station's zone
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    const [{ value: month }, , { value: day }, , { value: year }] = dtf.formatToParts(date);

  const utcMs = getUTCFromStationClock(
    Number(year), Number(month), Number(day), hours, minutes, timeZone
  );
  return new Date(utcMs).toISOString(); 
}

export function setToNoon(dateString: string, isStartDate:boolean): string {
  // Parse the input date string
  const date = new Date(dateString);

  if(isStartDate){
  // Set the time to 12:00:00.000 (noon)
  date.setHours(12, 0, 0, 0);
  
  }else{
  // Set the time to 12:00:00.000 (noon)
  date.setHours(11, 59, 0, 0);
  
  }
  // Return in ISO format (same format as input)
  return date.toISOString();
}